import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowRight, Scale } from "lucide-react";
import TrustStatement from "./TrustStatement";

const FEATURES_MONTHLY = [
  "**Up to £10,000 passing-off legal assistance**",
  "Dated record of your business name use",
  "Monitoring and overlap support",
  "Domain and online footprint guidance",
  "UK-based support team",
  "Onboarding name scan",
];

const FEATURES_ANNUAL = [
  "Everything in Monthly Protection",
  "Lower overall cost (save £24.60 per year)",
  "Priority response on support requests",
  "One simple annual payment",
];

function checkoutHref(planId, prefill = {}) {
  const params = new URLSearchParams({ plan: planId });
  if (prefill.business_name) params.set("business_name", prefill.business_name);
  if (prefill.industry) params.set("industry", prefill.industry);
  if (prefill.postcode) params.set("postcode", prefill.postcode);
  return `/checkout/details?${params.toString()}`;
}

// Tiny inline markdown for **bold** within feature bullets
function renderFeature(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((seg, k) =>
    seg.startsWith("**") && seg.endsWith("**")
      ? <strong key={k} className="text-[#04273d]">{seg.slice(2, -2)}</strong>
      : <span key={k}>{seg}</span>
  );
}

export default function PricingBlock({ prefill = {} }) {
  return (
    <div className="py-4" data-testid="pricing-block">
      <div className="grid md:grid-cols-2 gap-5">
        {/* Monthly */}
        <div className="bnp-card p-6 md:p-8 flex flex-col" data-testid="pricing-card-monthly">
          <div className="flex items-center justify-between">
            <span className="bnp-chip bnp-chip-light">Monthly</span>
            <span className="text-xs text-[#447694]">Flexible month-to-month option</span>
          </div>
          <h3 className="font-display text-2xl mt-4 text-[#04273d] font-semibold">Monthly Protection</h3>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-display text-5xl font-bold text-[#04273d]">£15</span>
            <span className="text-[#447694] text-sm">per month <span className="text-xs">(including VAT)</span></span>
          </div>
          <p className="text-sm text-[#447694] mt-2 leading-relaxed">
            A simple ongoing subscription with full access to your protection service.
          </p>

          <p className="font-display font-semibold text-[#04273d] text-sm mt-5">Includes</p>
          <ul className="mt-2.5 space-y-2.5 flex-1">
            {FEATURES_MONTHLY.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#447694]">
                <Check size={16} className="text-[#04273d] mt-0.5 shrink-0" />
                <span>{renderFeature(f)}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 rounded-2xl bg-[#f7f7f7] border border-[#9ac7f7]/25 px-4 py-3">
            <p className="font-display text-xs uppercase tracking-widest text-[#447694] font-semibold">Best for</p>
            <p className="text-sm text-[#04273d] mt-1 leading-relaxed">
              Businesses that want flexibility or prefer monthly budgeting.
            </p>
          </div>

          <Link
            to={checkoutHref("monthly", prefill)}
            className="btn-secondary justify-center mt-5 w-full"
            data-testid="pricing-monthly-button"
          >
            Start monthly protection <ArrowRight size={14} />
          </Link>
          <p className="text-[11.5px] text-[#447694] mt-3 text-center leading-relaxed">
            £15 today, then £15 per month.<br/>Cancel any time.
          </p>
        </div>

        {/* Annual */}
        <div className="bnp-card p-6 md:p-8 flex flex-col relative border-2 border-[#71edc9] shadow-[0_18px_50px_rgba(113,237,201,0.18)]" data-testid="pricing-card-annual">
          <div className="absolute -top-3 left-6 bnp-chip bnp-chip-navy flex items-center gap-1.5">
            <Sparkles size={12} /> Best value
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="bnp-chip bnp-chip-teal">Annual</span>
            <span className="text-xs text-[#447694]">Best value option</span>
          </div>
          <h3 className="font-display text-2xl mt-4 text-[#04273d] font-semibold">Annual Protection</h3>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-display text-5xl font-bold text-[#04273d]">£155.40</span>
            <span className="text-[#447694] text-sm">per year <span className="text-xs">(including VAT)</span></span>
          </div>
          <p className="text-sm text-[#447694] mt-2 leading-relaxed">
            Everything in the monthly plan, paid in one simple annual payment.
          </p>

          <p className="font-display font-semibold text-[#04273d] text-sm mt-5">Includes</p>
          <ul className="mt-2.5 space-y-2.5 flex-1">
            {FEATURES_ANNUAL.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#447694]">
                <Check size={16} className="text-[#04273d] mt-0.5 shrink-0" />
                <span>{renderFeature(f)}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 rounded-2xl bg-[#71edc9]/15 border border-[#71edc9]/35 px-4 py-3">
            <p className="font-display text-xs uppercase tracking-widest text-[#0d9b80] font-semibold">Best for</p>
            <p className="text-sm text-[#04273d] mt-1 leading-relaxed">
              Businesses that want the best value and a set-and-forget option.
            </p>
          </div>

          <Link
            to={checkoutHref("annual", prefill)}
            className="btn-primary justify-center mt-5 w-full"
            data-testid="pricing-annual-button"
          >
            Choose annual protection <ArrowRight size={14} />
          </Link>
          <p className="text-[11.5px] text-[#447694] mt-3 text-center leading-relaxed">
            One payment of £155.40 today.<br/>Locked in for 12 months.
          </p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-center gap-2 text-sm text-[#04273d]">
        <Scale size={16} className="text-[#71edc9]" />
        <span><strong>Up to £10,000</strong> passing-off legal assistance included with every plan.</span>
      </div>
      <TrustStatement className="mt-4" />
      <p className="text-[12px] text-[#447694] mt-3 text-center">
        You'll fill in your business details on the next step. Then secure payment via Stripe.
      </p>
    </div>
  );
}
